import React, { useState, useMemo, useEffect } from "react"
import {
  Card,
  CardBody,
  Table,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap"
import { Switch } from "antd"
import { FaEdit, FaPlus } from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"

const CategoryModal = ({ open, toggle, selected, setSelected, onSubmit }) => {
  const [error, setError] = useState("")

  useEffect(() => {
    setError("")
  }, [open])

  const handleSubmit = () => {
    if (!selected?.documentCategory?.trim()) {
      setError("Category name is required")
      return
    }
    onSubmit()
    setError("")
  }

  return (
    <Modal isOpen={open} centered size="l" toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {selected?.id ? "Edit Category" : "Add Category"}

        <div>
          <Button className="modal-btn" onClick={handleSubmit}>
            {selected?.id ? "Update" : "Add"}
          </Button>
        </div>
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label>
              Category Name <span className="text-danger">*</span>
            </Label>
            <Input
              value={selected?.documentCategory || ""}
              onChange={e => {
                setSelected(prev => ({ ...prev, documentCategory: e.target.value }))
                setError("")
              }}
              placeholder="e.g. Income Tax"
              className={`rounded-4 ${error ? "is-invalid" : ""}`}
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </FormGroup>
        </Form>
      </ModalBody>
      {/* <ModalFooter>
        <Button className="btn-blue" onClick={handleSubmit}>
          {selected?.id ? "Update" : "Add"}
        </Button>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter> */}
    </Modal>
  )
}

const CategoryIndex = () => {
  document.title = "Categories | Siddhi Tax Consultant"

  const { checkAccess } = usePermissionData()

  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState({})

  useEffect(() => {
    getCategories()
  }, [])

  async function getCategories() {
    const APIdata = await fetchData(setLoading, "document-category")
    if (APIdata?.success) {
      setCategories(APIdata?.data)
    }
  }

  const filteredData = useMemo(
    () =>
      categories.filter(item =>
        item?.documentCategory?.toLowerCase().includes(searchString.toLowerCase())
      ),
    [categories, searchString]
  )

  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(
    () => filteredData.slice(start, start + pageSize),
    [filteredData, currentPage, pageSize]
  )

  const toggleStatus = async (id, checked) => {
    Swal.fire({
      title: `Are you sure you want to ${checked ? "Enable" : "Disable"} this Category?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "document-category", id, {
          status: checked,
        })
        if (APIdata?.data?.success) {
          Swal.fire({
            title: `Category ${checked ? "Enabled" : "Disabled"} Successfully.`,
            text: APIdata?.data?.message,
            icon: "success",
          })
          getCategories()
        } else {
          Swal.fire({
            title: "Error Updating Category.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const handleSubmit = async () => {
    if (selected?.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "document-category",
        selected?.id,
        selected
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Category Updated Successfully.",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setModalOpen(false)
        getCategories()
        setSelected({})
      } else {
        Swal.fire({
          title: "Error Updating Category.",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(setLoading, "document-category", selected)
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Category Added Successfully.",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setModalOpen(false)
        setSelected({})
        getCategories()
      } else {
        Swal.fire({
          title: "Error Adding Category.",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleDelete = cat => {
    Swal.fire({
      title: `Are you sure you want to delete "${cat?.documentCategory}"?`,
      text: "This will remove the category permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "document-category", cat?.id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Category Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          getCategories()
        } else {
          Swal.fire({
            title: "Error Deleting Category.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  return (
    <PermittedComponent isAccess={checkAccess(7, "view") || checkAccess(7, "viewglobal")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Dashboard" breadcrumbItem="Categories" />

        <Card className="shadow-lg">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-3">
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="rounded-4 w-auto pe-5"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>

                <Input
                  className="rounded-4"
                  placeholder="Search category..."
                  value={searchString}
                  onChange={e => setSearchString(e.target.value)}
                />
              </div>

              {checkAccess(7, "add") && (
                <Button
                  className="btn-blue rounded-4 px-3"
                  onClick={() => {
                    setSelected({})
                    setModalOpen(true)
                  }}
                >
                  <FaPlus className="me-2" />
                  Add Category
                </Button>
              )}
            </div>

            <Table bordered responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Category Name</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No categories found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr key={item.id}>
                      <td>{start + index + 1}</td>
                      <td className="fw-bold">{item?.documentCategory}</td>
                      <td className="text-center">
                        <Switch
                          disabled={!checkAccess(7, "edit")}
                          checked={item.status}
                          onChange={checked => toggleStatus(item.id, checked)}
                        />
                      </td>
                      <td className="text-center">
                        {checkAccess(7, "edit") && (
                          <Button
                            className="bg-success text-white me-2 border-0 shadow-none"
                            onClick={() => {
                              setSelected({ ...item })
                              setModalOpen(true)
                            }}
                          >
                            <FaEdit size={16} />
                          </Button>
                        )}
                        {checkAccess(7, "delete") && (
                          <Button
                            className="bg-danger text-white border-0 shadow-none"
                            onClick={() => handleDelete(item)}
                          >
                            <MdDelete size={18} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            <nav
              aria-label="Page navigation"
              className="d-flex justify-content-between align-items-center mt-3"
            >
              <h5 className="mb-0">
                Showing {start + 1} to{" "}
                {Math.min(start + pageSize, filteredData.length)} of{" "}
                {categories.length} entries
              </h5>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  >
                    Previous
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${pageNum === currentPage ? "active" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                })}

                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""
                    }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </CardBody>
        </Card>

        <CategoryModal
          open={modalOpen}
          toggle={() => setModalOpen(false)}
          selected={selected}
          setSelected={setSelected}
          onSubmit={handleSubmit}
        />
      </div>
    </PermittedComponent>
  )
}

export default CategoryIndex
